import type { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

import VNDropdownBase from './VNDropdownBase';
import type { VNDropdownBaseProps } from './VNDropdownBase';

export interface VNDropdownRhfProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<VNDropdownBaseProps, 'value' | 'onChange' | 'onBlur' | 'error' | 'inputRef'> {
  name: Path<TFieldValues>;
  control?: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  required?: boolean;
  onValueChange?: (value: VNDropdownBaseProps['value']) => void;
  /** Called when the list closes, after the field `onBlur` handler runs. */
  onAfterBlur?: () => void;
}

const VNDropdownRhf = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control: controlProp,
  rules,
  required,
  label,
  multiple,
  onValueChange,
  onAfterBlur,
  ...baseProps
}: VNDropdownRhfProps<TFieldValues>) => {
  const formContext = useFormContext<TFieldValues>();
  const control = controlProp ?? formContext?.control;
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      ...(rules || {}),
      ...(required && { required: `${label || name} is required` }),
    },
    defaultValue: (multiple ? [] : null) as never,
  });

  const handleChange = (next: VNDropdownBaseProps['value']) => {
    if (onValueChange) {
      onValueChange(next);
      return;
    }
    onChange(next);
  };

  const handleBlur = () => {
    onBlur();
    onAfterBlur?.();
  };

  return (
    <VNDropdownBase
      {...baseProps}
      name={name}
      label={label}
      multiple={multiple}
      value={value ?? (multiple ? [] : null)}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error?.message}
      inputRef={ref}
    />
  );
};

export default VNDropdownRhf;
